// src/pages/ChatBot/query-logs/search-parser.ts
//
// The coded search syntax for the Queries tab, parsed into the GET params the
// query-logs list endpoint accepts. Pure and framework-independent: given the
// raw text an admin typed, it returns the submitted search terms. Kept out of
// QueriesPanel so the parsing rules can be read (and unit-tested) on their own.

// The submitted (not staged) search terms the fetch depends on. `question` maps
// to the GET `question` param, `user` to `user`, `timestamp` to `timestamp`.
// Empty strings mean "not filtering on that field".
export interface SearchTerms {
  question: string;
  user: string;
  timestamp: string;
}

export const EMPTY_SEARCH: SearchTerms = { question: '', user: '', timestamp: '' };

// The coded search keys admins type, mapped to the SearchTerms field they set.
// `query=` targets the question text — the natural default — so bare text with
// no recognized key prefix lands there too.
const SEARCH_KEY_TO_FIELD: Record<string, keyof SearchTerms> = {
  query: 'question',
  user: 'user',
  timestamp: 'timestamp',
};

// Parses the coded search syntax into GET params. Whitespace-delimited tokens
// are walked left-to-right: a `key=VALUE` token (key case-insensitive, one of
// query/user/timestamp) opens a field whose value runs across subsequent bare
// tokens until the next recognized `key=` token or the end of the string — so
// `user=jane doe query=vacation days` yields user "jane doe", question
// "vacation days". Leading bare text (before any key) and `query=` both feed
// the question field; if a key repeats, the last occurrence wins. Unrecognized
// `foo=bar` tokens have no recognized prefix, so they are treated as bare text.
export function parseSearchQuery(raw: string): SearchTerms {
  const terms: SearchTerms = { question: '', user: '', timestamp: '' };
  const tokens = raw.trim().split(/\s+/).filter(Boolean);

  let currentField: keyof SearchTerms = 'question';
  let valueTokens: string[] = [];
  // Commit the accumulated value into the field being built; a later write to
  // the same field overwrites the earlier one, giving "last wins" semantics.
  const flush = () => {
    terms[currentField] = valueTokens.join(' ').trim();
  };

  tokens.forEach((token) => {
    const match = /^(timestamp|user|query)=(.*)$/i.exec(token);
    if (match) {
      flush();
      currentField = SEARCH_KEY_TO_FIELD[match[1].toLowerCase()];
      valueTokens = match[2] ? [match[2]] : [];
    } else {
      valueTokens.push(token);
    }
  });
  flush();

  return terms;
}
